import { useCallback, useMemo, useState } from 'react';

import { useLazyGetCertificatesForSaleQuery } from '@app/api/Certificates';
import type { CertificateSaleDraftItem } from '@app/components/RecordModal/components/RecordDetails/types';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { createId } from '@app/utils';

interface UseCertificateSaleItemsProps {
  items: CertificateSaleDraftItem[];
  onChange?(items: CertificateSaleDraftItem[]): void;
  activeBranchId: number | null;
}

// type, а не interface: опция Select должна иметь индексную сигнатуру
export type CertificateSaleOption = {
  value: number;
  label: string;
  nominal: number;
};

export interface CertificateSaleRow {
  item: CertificateSaleDraftItem;
  total: number;
  /** Сертификат уже продан клиенту: сам сертификат, продавец и цена не меняются */
  isSold: boolean;
  /** Проданный сертификат оплачен (CanEditAndDelete = false) */
  isPaid: boolean;
  /** Скидку можно менять, строку — удалить: новая или неоплаченная */
  isEditable: boolean;
}

const roundMoney = (value: number) => Number(value.toFixed(2));

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getCertificateSaleTotal = ({
  price,
  discountCurrency,
}: CertificateSaleDraftItem) => {
  const total = price - discountCurrency;

  return total > 0 ? roundMoney(total) : 0;
};

const createCertificateSaleDraftItem = (): CertificateSaleDraftItem => ({
  id: createId(),
  saleId: 0,
  nominal: 0,
  price: 0,
  discountCurrency: 0,
  discountPercent: 0,
  sellerId: null,
  canEditAndDelete: true,
});

/**
 * Строки сертификатов в окне записи (прототип dev.promo-grushka.ru).
 * Список сертификатов, доступных к продаже в филиале, грузится по
 * «Добавить сертификат» (GetCertificatesForSale). Один проданный сертификат — один уникальный номер,
 * поэтому количество всегда 1. Цена по умолчанию равна номиналу и
 * меняется вручную, скидка — от 0 до цены, в рублях и в процентах.
 * Проданная строка (saleId > 0) ведёт себя как проданный абонемент: меняется
 * только скидка, оплаченная не меняется и не удаляется.
 */
export const useCertificateSaleItems = ({
  items,
  onChange,
  activeBranchId,
}: UseCertificateSaleItemsProps) => {
  const { showNotification } = useNotification();
  // Проданная строка удаляется только после окна подтверждения: после
  // сохранения записи продажа сертификата отменится.
  const [itemIdToDelete, setItemIdToDelete] = useState<string | null>(null);
  // Название для текста окна запоминается при нажатии на корзину и не
  // сбрасывается при закрытии: текст не меняется, пока окно закрывается.
  const [nameToDelete, setNameToDelete] = useState<string | null>(null);
  const [
    getCertificates,
    { data: certificates, isFetching: isCertificatesLoading },
  ] = useLazyGetCertificatesForSaleQuery();

  const certificateOptions: CertificateSaleOption[] = useMemo(
    () =>
      (certificates ?? []).map(({ CertificateId, Name, Cost }) => ({
        value: CertificateId,
        label: Name ?? '',
        nominal: Cost,
      })),
    [certificates]
  );

  const rows: CertificateSaleRow[] = useMemo(
    () =>
      items.map((item) => {
        const isSold = item.saleId > 0;
        const isPaid = isSold && !item.canEditAndDelete;

        return {
          item,
          total: getCertificateSaleTotal(item),
          isSold,
          isPaid,
          isEditable: !isPaid,
        };
      }),
    [items]
  );

  const totalQuantity = useMemo(
    () => items.filter(({ certificateId }) => certificateId).length,
    [items]
  );

  const totalAmount = useMemo(
    () =>
      roundMoney(
        rows
          .filter(({ item }) => item.certificateId)
          .reduce((acc, { total }) => acc + total, 0)
      ),
    [rows]
  );

  const updateItem = useCallback(
    (
      itemId: string,
      handler: (item: CertificateSaleDraftItem) => CertificateSaleDraftItem
    ) => {
      onChange?.(
        items.map((item) => (item.id === itemId ? handler(item) : item))
      );
    },
    [items, onChange]
  );

  const addItemHandler = useCallback(async () => {
    onChange?.([...items, createCertificateSaleDraftItem()]);

    if (activeBranchId === null) {
      showNotification('Активный филиал не найден', FRONT_WARNING);
      return;
    }

    try {
      await getCertificates({ ActiveBranchId: activeBranchId }, true).unwrap();
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [items, onChange, activeBranchId, getCertificates, showNotification]);

  const removeItem = useCallback(
    (itemId: string) => {
      onChange?.(items.filter((item) => item.id !== itemId));
    },
    [items, onChange]
  );

  const deleteItemHandler = useCallback(
    (itemId: string) => {
      const item = items.find(({ id }) => id === itemId);

      if (item && item.saleId > 0) {
        setItemIdToDelete(itemId);
        setNameToDelete(item.name ?? null);
        return;
      }

      removeItem(itemId);
    },
    [items, removeItem]
  );

  const isDeleteConfirmOpen = useMemo(
    () => items.some(({ id }) => id === itemIdToDelete),
    [items, itemIdToDelete]
  );

  const deleteConfirmText = nameToDelete
    ? `Сертификат «${nameToDelete}» продан клиенту. После сохранения записи продажа отменится.`
    : 'Сертификат продан клиенту. После сохранения записи продажа отменится.';

  const confirmDeleteHandler = useCallback(() => {
    if (itemIdToDelete) removeItem(itemIdToDelete);

    setItemIdToDelete(null);
  }, [itemIdToDelete, removeItem]);

  const deleteModalOpenChangeHandler = useCallback((isOpen: boolean) => {
    if (!isOpen) setItemIdToDelete(null);
  }, []);

  const certificateChangeHandler = useCallback(
    (itemId: string, certificateId: number) => {
      const certificate = certificateOptions.find(
        ({ value }) => value === certificateId
      );

      if (!certificate) return;

      updateItem(itemId, (item) => ({
        ...item,
        certificateId,
        name: certificate.label,
        nominal: certificate.nominal,
        price: certificate.nominal,
        discountCurrency: 0,
        discountPercent: 0,
      }));
    },
    [certificateOptions, updateItem]
  );

  const sellerChangeHandler = useCallback(
    (itemId: string, sellerId?: number) => {
      updateItem(itemId, (item) => ({ ...item, sellerId: sellerId ?? null }));
    },
    [updateItem]
  );

  // Процент скидки сохраняется, сумма скидки пересчитывается от новой цены.
  const priceChangeHandler = useCallback(
    (itemId: string, value: number) => {
      updateItem(itemId, (item) => {
        const price = Math.max(value, 0);

        return {
          ...item,
          price,
          discountCurrency: roundMoney((price * item.discountPercent) / 100),
        };
      });
    },
    [updateItem]
  );

  const discountCurrencyChangeHandler = useCallback(
    (itemId: string, value: number) => {
      updateItem(itemId, (item) => {
        const discountCurrency = clamp(value, 0, item.price);

        return {
          ...item,
          discountCurrency,
          discountPercent:
            item.price > 0 ? (discountCurrency / item.price) * 100 : 0,
        };
      });
    },
    [updateItem]
  );

  const discountPercentChangeHandler = useCallback(
    (itemId: string, value: number) => {
      updateItem(itemId, (item) => {
        const discountPercent = clamp(value, 0, 100);

        return {
          ...item,
          discountPercent,
          discountCurrency: roundMoney((item.price * discountPercent) / 100),
        };
      });
    },
    [updateItem]
  );

  return {
    rows,
    certificateOptions,
    isCertificatesLoading,
    totalQuantity,
    totalAmount,
    addItemHandler,
    deleteItemHandler,
    isDeleteConfirmOpen,
    deleteConfirmText,
    confirmDeleteHandler,
    deleteModalOpenChangeHandler,
    certificateChangeHandler,
    sellerChangeHandler,
    priceChangeHandler,
    discountCurrencyChangeHandler,
    discountPercentChangeHandler,
  };
};
